import { Link } from "@tanstack/react-router";
import { ChevronRight, Clock, Heart, MapPin, Star, UtensilsCrossed } from "lucide-react";

import MarqueeRail, { duplicateForMarquee } from "@/components/MarqueeRail";
import { SmartImage } from "@/components/ui/SmartImage";
import { useLiveStatus } from "@/components/districts/OpenStatusPill";
import { useFavorites } from "@/hooks/useFavorites";
import { useReviewStat } from "@/hooks/use-review-stats";
import { useGeolocation, distanceKm, formatDistance } from "@/hooks/useGeolocation";
import { helsinkiNow } from "@/lib/opening-hours";
import type { DistrictVenueDTO } from "@/lib/districts.functions";

const BADGE: Record<string, string> = {
  open: "bg-white/95 text-emerald-700",
  closing_soon: "bg-white/95 text-amber-700",
  closed: "bg-white/95 text-navy/70",
  unknown: "",
};

const DOT: Record<string, string> = {
  open: "bg-emerald-500",
  closing_soon: "bg-amber-500",
  closed: "bg-navy/35",
  unknown: "bg-slate-300",
};

function badgeText(state: string) {
  if (state === "open") return "Open now";
  if (state === "closing_soon") return "Closing soon";
  if (state === "closed") return "Closed";
  return null;
}

function prettyName(s: string) {
  const t = s.trim();
  if (t === t.toUpperCase() && /[A-Z]/.test(t)) {
    return t.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return t;
}

function closesIn(closesAt?: string): string | null {
  if (!closesAt) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(closesAt);
  if (!m) return null;
  const { minutes } = helsinkiNow();
  let diff = Number(m[1]) * 60 + Number(m[2]) - minutes;
  if (diff < 0) diff += 24 * 60;
  if (diff > 90) return null;
  return diff >= 60 ? `Closes in ${Math.round(diff / 60)} h` : `Closes in ${diff} min`;
}

function hasMenu(v: DistrictVenueDTO) {
  return !!(v.menu_external_url || v.menu_pdf_url || v.menu_image_url);
}

function menuHref(v: DistrictVenueDTO) {
  return v.menu_external_url ?? v.menu_pdf_url ?? v.menu_image_url ?? null;
}

export function VenueCard({ v, className = "" }: { v: DistrictVenueDTO; className?: string }) {
  const status = useLiveStatus(v);
  const { has, toggle } = useFavorites();
  const stat = useReviewStat(v.slug);
  const { state: geo } = useGeolocation(true);
  const saved = has(v.id);
  const dist =
    geo.status === "granted" && v.lat != null && v.lng != null
      ? distanceKm(geo, { lat: v.lat, lng: v.lng })
      : null;
  const area = v.area || "Pasila";
  const label = badgeText(status.state);
  const soon = status.state === "open" || status.state === "closing_soon" ? closesIn(status.closesAt) : null;
  const today = status.todayLabel && status.todayLabel !== "—" ? status.todayLabel : null;
  const menu = hasMenu(v) ? menuHref(v) : null;
  const tags = [v.category, ...(v.tags ?? []).slice(0, 2)].filter(Boolean);

  return (
    <article
      className={`group flex flex-col overflow-hidden rounded-lg border border-navy/10 bg-white shadow-[0_8px_20px_-16px_rgba(15,27,61,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(15,27,61,0.28)] ${className}`}
    >
      <Link to="/place/$id" params={{ id: v.slug }} className="flex min-h-0 flex-1 flex-col">
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100 sm:aspect-[16/10]">
          {v.image ? (
            <SmartImage
              src={v.image}
              alt={prettyName(v.name)}
              className="size-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            />
          ) : (
            <div className="size-full bg-gradient-to-br from-navy/10 to-navy/5" />
          )}
          <div aria-hidden className="pointer-events-none absolute inset-0 hidden bg-gradient-to-t from-black/40 via-black/0 to-black/10 sm:block" />

          {label ? (
            <span className={`absolute left-2.5 top-2.5 hidden items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide shadow-sm backdrop-blur sm:inline-flex ${BADGE[status.state]}`}>
              <span className={`size-1.5 rounded-full ${DOT[status.state]} ${status.state === "open" ? "animate-pulse" : ""}`} />
              {label}
            </span>
          ) : null}

          {today || soon ? (
            <span className="absolute bottom-2.5 left-2.5 hidden rounded-full bg-black/45 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm sm:inline">
              {soon ?? `Today ${today}`}
            </span>
          ) : null}

          <button
            type="button"
            aria-label={saved ? "Remove from favourites" : "Save to favourites"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggle(v.id);
            }}
            className="absolute right-2.5 top-2.5 hidden size-8 place-items-center rounded-full bg-white/95 shadow-sm backdrop-blur transition-transform hover:scale-105 sm:grid"
          >
            <Heart className={`size-3.5 ${saved ? "fill-rose-500 text-rose-500" : "text-navy/55"}`} />
          </button>
        </div>

        <div className="flex flex-1 flex-col px-2.5 pb-1 pt-2 sm:px-4 sm:pb-0 sm:pt-3.5">
          <div className="flex items-start justify-between gap-2">
            <h3 className="line-clamp-1 text-[13px] font-semibold leading-snug tracking-tight text-navy sm:line-clamp-2 sm:text-[15px]">
              {prettyName(v.name)}
            </h3>
            {stat.count >= 5 ? (
              <span className="mt-0.5 hidden shrink-0 items-center gap-0.5 text-[12px] font-semibold tabular-nums text-navy sm:inline-flex">
                {stat.avg.toFixed(1)}
                <Star className="size-3 fill-navy text-navy" />
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 line-clamp-1 text-[11px] text-navy/50 sm:text-[12px]">
            {tags.length ? tags.join(" · ") : "\u00a0"}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {dist != null ? (
              <span className="inline-flex items-center rounded-full bg-navy/[0.06] px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-navy/70 sm:text-[11px]">
                {formatDistance(dist)}
              </span>
            ) : null}
            <p className="inline-flex min-w-0 items-center gap-1 text-[11px] font-medium text-navy/70">
              <MapPin className="size-2.5 shrink-0 text-teal sm:size-3" strokeWidth={2.25} />
              <span className="truncate">{area}</span>
            </p>
          </div>
          {today ? (
            <p className="mt-1 flex items-center gap-1 text-[11px] font-medium text-navy/55 sm:hidden">
              <Clock className="size-3 shrink-0 text-navy/40" strokeWidth={2.25} />
              {today}
            </p>
          ) : null}
        </div>
      </Link>

      {v.has_dish_menu ? (
        <div className="mt-auto px-2.5 pb-2.5 pt-2 sm:px-4 sm:pb-4 sm:pt-3">
          <Link
            to="/menu/$slug"
            params={{ slug: v.slug }}
            className="inline-flex h-7 w-full items-center justify-center gap-0.5 rounded-md bg-navy px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-navy/90 sm:h-9 sm:gap-1.5 sm:px-3 sm:text-[12px]"
          >
            <UtensilsCrossed className="hidden size-3.5 sm:block" strokeWidth={2.25} />
            View menu
            <ChevronRight className="size-3 sm:hidden" />
          </Link>
        </div>
      ) : menu ? (
        <div className="mt-auto px-2.5 pb-2.5 pt-2 sm:px-4 sm:pb-4 sm:pt-3">
          <a
            href={menu}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-7 w-full items-center justify-center gap-0.5 rounded-md bg-navy px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-navy/90 sm:h-9 sm:gap-1.5 sm:px-3 sm:text-[12px]"
          >
            <UtensilsCrossed className="hidden size-3.5 sm:block" strokeWidth={2.25} />
            View menu
            <ChevronRight className="size-3 sm:hidden" />
          </a>
        </div>
      ) : (

        <div className="mt-auto px-2.5 pb-2.5 pt-2 sm:px-4 sm:pb-4 sm:pt-3">
          <Link
            to="/place/$id"
            params={{ id: v.slug }}
            className="inline-flex h-7 w-full items-center justify-center gap-0.5 rounded-md bg-navy px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-navy/90 sm:h-9 sm:border sm:border-navy/12 sm:bg-white sm:text-[12px] sm:text-navy sm:hover:bg-slate-50"
          >
            View place
            <ChevronRight className="size-3 sm:hidden" />
          </Link>
        </div>
      )}
    </article>
  );
}

export function DistrictVenueRail({
  venues,
  direction = "right",
}: {
  venues: DistrictVenueDTO[];
  direction?: "left" | "right";
}) {
  if (venues.length === 0) return null;
  const loop = duplicateForMarquee(venues.slice(0, 12));

  return (
    <MarqueeRail className="!mx-0 !px-0" trackClassName="marquee-track gap-3 sm:gap-4" direction={direction}>
      {loop.map((v, i) => (
        <VenueCard key={`${v.id}-${i}`} v={v} className="w-[136px] shrink-0 sm:w-[240px] lg:w-[340px]" />
      ))}
    </MarqueeRail>
  );
}
